import { useNavigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { TAB_ROUTES } from "@/context/TabsContext";
import { useAuth } from "@/context/AuthContext";
import ToothIcon from "@/components/ToothIcon";
import { ROLE_LABEL } from "@/lib/api";

/**
 * Sidebar mini (64px) that expands to 240px on hover. Fixed position,
 * floats over content with shadow when expanded. Keyboard accessible.
 */
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (to) =>
    location.pathname === to || (to !== "/" && location.pathname.startsWith(to));

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex group fixed top-16 left-0 h-[calc(100vh-4rem)] z-40 flex-col border-r border-border bg-card shadow-none hover:shadow-xl w-16 hover:w-60 transition-[width] duration-200 ease-in-out overflow-hidden"
    >
      <nav className="flex-1 flex flex-col gap-0.5 px-2 py-3 overflow-y-auto" role="navigation">
        {TAB_ROUTES.map((it) => {
          const Icon = it.icon;
          const active = isActive(it.path);
          return (
            <button
              key={it.path}
              type="button"
              data-testid={`nav-${it.key}`}
              onClick={() => navigate(it.path)}
              aria-label={it.label}
              aria-current={active ? "page" : undefined}
              className={`flex items-center gap-3 h-10 px-3 rounded-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/80 hover:bg-muted"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {it.label}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="p-2 border-t border-border">
        <div className="flex items-center gap-3 px-2 h-12 rounded-sm hover:bg-muted transition">
          <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center font-display font-bold text-xs shrink-0">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <div className="text-xs font-semibold truncate">{user?.name}</div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="p-2 rounded-sm hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition opacity-0 group-hover:opacity-100"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
